// src/pages/Admin/Photos/components/PhotosFormModal.jsx
import React, { useEffect, useRef } from "react";
import { FiFileText, FiImage, FiUploadCloud, FiX } from "react-icons/fi";
import { formatFileSize } from "../../../../services/mediaLibraryServices";

export default function PhotosFormModal({
  open,
  onClose,
  form,
  setForm,
  file,
  setFile,
  onSave,
  saving,
  mode,
}) {
  const fileInputRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  const onChangeField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onFileChange = (selectedFile) => {
    setFile(selectedFile);

    if (!selectedFile || mode !== "create") return;

    const detectedType =
      selectedFile.type === "application/pdf"
        ? "document"
        : selectedFile.type?.startsWith("image/")
        ? "image"
        : "other";

    setForm((prev) => ({
      ...prev,
      type: detectedType,
      title:
        prev.title ||
        selectedFile.name
          ?.replace(/\.[^/.]+$/, "")
          ?.replace(/[-_]+/g, " ")
          ?.trim() ||
        "",
    }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    onSave();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const isPdf = file?.type === "application/pdf";
  const isImage = file?.type?.startsWith("image/");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="max-h-[92vh] w-full max-w-2xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div>
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              {mode === "create"
                ? "Ajouter un média"
                : "Modifier les informations"}
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Images optimisées automatiquement, PDF conservés au format
              original.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-800 dark:border-slate-700 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            <FiX className="h-4 w-4" />
          </button>
        </div>

        <form
          onSubmit={onSubmit}
          className="max-h-[calc(92vh-82px)] overflow-y-auto px-5 py-5"
        >
          <div className="space-y-5">
            {mode === "create" && (
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
                  Fichier image ou PDF
                </label>

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="group flex w-full flex-col items-center justify-center rounded-3xl border-2 border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center transition hover:border-sky-400 hover:bg-sky-50 dark:border-slate-700 dark:bg-slate-950/60 dark:hover:border-sky-500 dark:hover:bg-sky-950/30"
                >
                  <span className="mb-3 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-sky-600 shadow-sm dark:bg-slate-900">
                    {isPdf ? (
                      <FiFileText className="h-7 w-7" />
                    ) : isImage ? (
                      <FiImage className="h-7 w-7" />
                    ) : (
                      <FiUploadCloud className="h-7 w-7" />
                    )}
                  </span>

                  <span className="text-sm font-bold text-slate-900 dark:text-white">
                    {file ? file.name : "Cliquez pour sélectionner un fichier"}
                  </span>

                  <span className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    JPG, PNG, WEBP, GIF ou PDF · 15 Mo maximum
                  </span>

                  {file && (
                    <span className="mt-3 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white dark:bg-white dark:text-slate-950">
                      {formatFileSize(file.size)}
                    </span>
                  )}
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => onFileChange(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-bold text-slate-600 dark:text-slate-300">
                  Titre
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => onChangeField("title", e.target.value)}
                  placeholder="Ex : Compte-rendu conseil municipal"
                  className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold text-slate-600 dark:text-slate-300">
                  Catégorie
                </label>
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => onChangeField("category", e.target.value)}
                  placeholder="ex : conseil-municipal, actualites"
                  className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold text-slate-600 dark:text-slate-300">
                Texte alternatif
              </label>
              <input
                type="text"
                value={form.alt_text}
                onChange={(e) => onChangeField("alt_text", e.target.value)}
                placeholder="Utile surtout pour les images"
                className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold text-slate-600 dark:text-slate-300">
                Tags
              </label>
              <input
                type="text"
                value={form.tags}
                onChange={(e) => onChangeField("tags", e.target.value)}
                placeholder="mairie, conseil, budget, travaux..."
                className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-bold text-slate-600 dark:text-slate-300">
                  Type de média
                </label>
                <select
                  value={form.type}
                  onChange={(e) => onChangeField("type", e.target.value)}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                >
                  <option value="image">Image</option>
                  <option value="document">Document</option>
                  <option value="video">Vidéo</option>
                  <option value="other">Autre</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold text-slate-600 dark:text-slate-300">
                  Visibilité
                </label>

                <button
                  type="button"
                  onClick={() => onChangeField("is_public", !form.is_public)}
                  className={
                    "flex w-full items-center justify-between rounded-2xl border px-3 py-2.5 text-sm font-bold transition " +
                    (form.is_public
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300"
                      : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300")
                  }
                >
                  <span>{form.is_public ? "Public" : "Privé"}</span>
                  <span className="text-xs font-semibold">
                    {form.is_public
                      ? "Visible sur le site"
                      : "Réservé au backoffice"}
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse gap-2 border-t border-slate-200 pt-4 dark:border-slate-800 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-2xl border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Annuler
            </button>

            <button
              type="submit"
              disabled={saving || (mode === "create" && !file)}
              className="rounded-2xl bg-sky-600 px-5 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving
                ? "Enregistrement..."
                : mode === "create"
                ? "Ajouter le média"
                : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}